import React, { useCallback, useEffect, useState } from "react";
import {
  Body,
  ButtonToggle,
  Image,
  ImageEnhancements,
  Layout,
  LoadingIndicator,
  Spacer,
  // StickyContainer,
  StylingGrid,
  Tile,
  UtilityBar
} from "@common/AggregationComponent";
import { throttle } from "lodash";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { CombinedReducersType } from "@type/index";
import { OutfitDetail, PORDUCT_SORT } from "@type/index";
import { isGUNativeApp } from "@util/nativeApp";
import {
  sendTrackEvent,
  sendTrackPageView,
  TRACK_EVENT,
  TRACK_EVENT_SEARCH
} from "@util/gtm";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  checkPreviousPageExists,
  getRegionFromUrl,
  preventBubbling,
  transformBrandForGoogleAnalytics,
  getEcRenewalCompatibility
} from "@util/common";
import { isIOS, isSafari as isSafariBrowser, getUA } from "react-device-detect";
import { isEUCountry } from "@util/country";
import { useCookies } from "react-cookie";
import { FR_UserAgent } from "@util/config";
import FilterNoResult from "../../../common/FilterNoResult";
import { Sort } from "../ProductList";
import style from "../style.module.scss";

export type OutfitListProps = {
  visible: boolean;
  isLoading: boolean;
  isScrollNextPageLoading: boolean;
  filterCount: number | string;
  total: number | string;
  outfits: OutfitDetail[];
  searchResult: any;
  isImageLoad: boolean;
  handleClearAll: () => void;
  onChangeSort: (sort: string) => void;
  onToggleFilter: () => void;
  hasMore: boolean;
  handlerScrollNext: () => void;
  setIsClickOutfitTabSort: any;
  handleClickStyleFavorite: (outfit: OutfitDetail) => void;
  isShowOutfitWishIcon: any;
  filterPillContent: any;
};

const StylingList = (props: OutfitListProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [, setCookie] = useCookies(["jumpFrom"]);
  const config = useSelector((store: CombinedReducersType) => store.app);
  const BASE_PATH_NAME = `/${config.region}/${config.language}`;
  const {
    visible,
    isLoading,
    isScrollNextPageLoading,
    filterCount,
    total,
    outfits,
    searchResult,
    handleClearAll,
    onToggleFilter,
    onChangeSort,
    isImageLoad,
    hasMore,
    handlerScrollNext,
    setIsClickOutfitTabSort,
    handleClickStyleFavorite,
    isShowOutfitWishIcon,
    filterPillContent
  } = props;
  const [sortList, setSortList] = useState<Sort[]>([]);
  useEffect(() => {
    setSortList([
      {
        label: t("text_image_search_sort_latest"),
        value: PORDUCT_SORT.RECENCY
      }
    ]);
  }, [t]);
  useEffect(() => {
    const isFinalTitle =
      document.title === t("text_image_search_result_meta_title");
    const isNotDefaultTitle =
      document.title !== "text_image_search_result_meta_title";
    if (window.dataLayer && isFinalTitle && isNotDefaultTitle) {
      sendTrackPageView({
        event: "trackPageView",
        brand: transformBrandForGoogleAnalytics(config.brand_name), // UQの場合"uq"
        pageType: "imagesearch", // TPUの場合TPU
        subID: "", // subid取得できる場合subidを出力、できない場合undefined
        pageUrlPath: `${location.pathname}${location.search}`, // path+param
        pageTitle: t("text_image_search_result_meta_title"), // title
        previousPageExists: checkPreviousPageExists() // 前のページがある場合true、ない場合false
      });
    }
  }, [document.title, t, location]);
  const handlerChangeSort = (e: any, value: string) => {
    onChangeSort(value);
    setIsClickOutfitTabSort(true);
    sendTrackEvent({
      brand: transformBrandForGoogleAnalytics(config.brand_name),
      event: TRACK_EVENT,
      eventAction: "ImageOutfitSearch",
      eventCategory: "OS_Item_Sort",
      eventLabel: `${t("text_image_search_sort_latest")}`
    });
  };
  const handleJumpOutfitDetail = (outfit: OutfitDetail, index: number) => {
    const htmlDom = document.querySelector("html");
    const isSafari = isSafariBrowser || isIOS;
    if (htmlDom && !isSafari) {
      sessionStorage.setItem("outfitScrollTop", htmlDom.scrollTop.toString());
    }
    sendTrackEvent({
      brand: transformBrandForGoogleAnalytics(config.brand_name),
      event: TRACK_EVENT_SEARCH,
      eventAction: "Outfit_Detail",
      eventCategory: "OS_Result_Outfit",
      eventLabel: `${index}_${outfit.styleId}`,
      itemIndex: `${index}`,
      itemId: `${outfit.styleId}`
    });
    setCookie("jumpFrom", "outfit", { path: "/" });
  };
  const getOutfitDetailUrl = (outfit: OutfitDetail) => {
    if (outfit.outfitType === "official") {
      return `${window.location.origin}${BASE_PATH_NAME}/stylingbook/officialstyling/${outfit.styleId}`;
    }
    if (outfit.outfitType === "stylehint") {
      return `${window.location.origin}${BASE_PATH_NAME}/stylingbook/stylehint/${outfit.styleId}`;
    }
    return `${window.location.origin}${BASE_PATH_NAME}/stylingbook/officialstyling/${outfit.styleId}`;
  };

  const getOutfitTiles = () => {
    const renderList: any = [];
    if (outfits.length > 0) {
      outfits.forEach((outfit: OutfitDetail, index: number) => {
        if (outfit) {
          renderList.push(
            <Tile
              element={"a"}
              href={getOutfitDetailUrl(outfit)}
              onClick={() => {
                handleJumpOutfitDetail(outfit, index);
              }}
            >
              {
                // TODO: Note!When the wish function is unified in the APP, the judgment here needs to be processed.
                <ImageEnhancements
                  topRightContent={
                    isShowOutfitWishIcon &&
                    (isGUNativeApp() || !config.isApp) &&
                    !isEUCountry(getRegionFromUrl()) &&
                    getUA.indexOf(FR_UserAgent) <= -1 && (
                      <ButtonToggle
                        icon="heart"
                        variant="icon"
                        onClick={(e: any) => {
                          preventBubbling(e);
                          handleClickStyleFavorite(outfit);
                        }}
                        isToggled={!!outfit.favorite}
                      />
                    )
                  }
                  padding={false}
                >
                  <Image
                    alt={`${t("text_no_image")}`}
                    ratio="3x4"
                    src={`${outfit.imageUrl}_r-600-800`}
                  />
                </ImageEnhancements>
              }
            </Tile>
          );
        }
      });
    }
    return renderList;
  };
  const OutfitContent = useCallback(() => {
    const outfitTiles = getOutfitTiles();
    if (!isLoading && outfitTiles.length === 0) {
      return (
        <FilterNoResult
          handleClearAll={handleClearAll}
          filterCount={filterCount}
        />
      );
    }
    return <StylingGrid gridItems={outfitTiles} />;
  }, [outfits, isLoading, searchResult.result]);

  const checkNewUI3 = () => {
    if (getEcRenewalCompatibility()) {
      return (
        <Layout style={{ width: "100%" }}>
          {isImageLoad && (
            // <StickyContainer
            //   stickySection="utilityBar"
            //   isNavHeader={!config.isApp}
            // >
            <UtilityBar
              resultDetailsSection={
                <Body variant="standard" element="span">
                  {t("text_image_search_result_number", {
                    total: total
                  })}
                </Body>
              }
              isNavHeader={!config.isApp}
              sortLabel={visible ? t("text_sort") : ""}
              toggleSort={visible}
              toggleFilter={false}
              onToggleFilter={onToggleFilter}
              handleChangeSort={handlerChangeSort}
              badgeCount={filterCount}
              filterBadgeCount={filterCount}
              currentSortValue={PORDUCT_SORT.RECENCY}
              sortOptions={sortList}
              className={style.filter_button_box}
              filterPillContent={filterPillContent}
              closeLabel={getEcRenewalCompatibility() ? " " : t("text_close")}
              filterIcon="filter"
              onClickFilterIcon={onToggleFilter}
              collapseFilterLabel={undefined}
              ecRenewal
            />
            // </StickyContainer>
          )}
          {isLoading && <LoadingIndicator variant="full-screen" />}
          <InfiniteScroll
            loader={<></>}
            dataLength={outfits.length}
            hasMore={hasMore}
            next={throttle(handlerScrollNext, 200)}
          >
            <OutfitContent />
            {isScrollNextPageLoading && (
              <LoadingIndicator
                override={{
                  lg: "normal",
                  md: "normal",
                  sm: "normal"
                }}
                variant="normal"
              />
            )}
            <Spacer element="div" variant={"spacing-08"} />
            {/* For fix filter modal scroll error: setting min height */}
            {config.isApp && outfits.length > 0 && outfits.length < 3 && (
              <div style={{ height: "195px" }} />
            )}
            {!config.isApp && outfits.length > 0 && outfits.length < 3 && (
              <div style={{ height: "69px" }} />
            )}
            {isLoading && outfits.length === 0 && (
              <div style={{ height: config.isApp ? "445px" : "345px" }} />
            )}
          </InfiniteScroll>
        </Layout>
      );
    } else {
      return (
        <Layout style={{ width: "100%" }}>
          {isImageLoad && (
            // <StickyContainer
            //   stickySection="utilityBar"
            //   isNavHeader={!config.isApp}
            // >
            <UtilityBar
              resultDetailsSection={
                <Body variant="standard" element="span">
                  {t("text_image_search_result_number", {
                    total: total
                  })}
                </Body>
              }
              isNavHeader={!config.isApp}
              sortLabel={visible ? t("text_sort") : ""}
              collapseFilterLabel={t("text_filter")}
              toggleSort={visible}
              toggleFilter={false}
              onToggleFilter={onToggleFilter}
              handleChangeSort={handlerChangeSort}
              badgeCount={filterCount}
              filterBadgeCount={filterCount}
              currentSortValue={PORDUCT_SORT.RECENCY}
              sortOptions={sortList}
              className={style.filter_button_box}
              filterPillContent={filterPillContent}
              closeLabel={t("text_close")}
            />
            // </StickyContainer>
          )}
          {isLoading && <LoadingIndicator variant="full-screen" />}
          <InfiniteScroll
            loader={<></>}
            dataLength={outfits.length}
            hasMore={hasMore}
            next={throttle(handlerScrollNext, 200)}
          >
            <OutfitContent />
            {isScrollNextPageLoading && (
              <LoadingIndicator
                override={{
                  lg: "normal",
                  md: "normal",
                  sm: "normal"
                }}
                variant="normal"
              />
            )}
            <Spacer element="div" variant={"spacing-08"} />
            {/* For fix filter modal scroll error: setting min height */}
            {config.isApp && outfits.length > 0 && outfits.length < 3 && (
              <div style={{ height: "195px" }} />
            )}
            {!config.isApp && outfits.length > 0 && outfits.length < 3 && (
              <div style={{ height: "69px" }} />
            )}
            {isLoading && outfits.length === 0 && (
              <div style={{ height: config.isApp ? "445px" : "345px" }} />
            )}
          </InfiniteScroll>
        </Layout>
      );
    }
  };

  return <>{checkNewUI3()}</>;
};

export default StylingList;
